import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { API_BASE_URL, getApiUrl } from '../../config/api';
import LoginModal from '../../components/LoginModal';

// console.log('Stripe Public Key:', process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function UpiPaymentForm({ cartItems, onPaymentSuccess, disabled }) {
  const [upiId, setUpiId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [upiStatus, setUpiStatus] = useState('');
  const [upiRedirectUrl, setUpiRedirectUrl] = useState('');
  const [upiQrCode, setUpiQrCode] = useState('');

  const handleUpiSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setUpiStatus('');
    setUpiRedirectUrl('');
    setUpiQrCode('');
    if (!upiId) {
      setError('Please enter your UPI ID.');
      return;
    }
    setLoading(true);
    const amount = cartItems.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);
    try {
      const res = await fetch(getApiUrl('/api/payment/create-payment-intent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, currency: 'INR', payment_method_types: ['upi'] }),
      });
      const data = await res.json();
      if (!data.clientSecret) {
        setError('Failed to initialize UPI payment.');
        setLoading(false);
        return;
      }
      // Now confirm payment with Stripe
      setLoading(false);
    } catch (err) {
      setError('Failed to process UPI payment.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleUpiSubmit} className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">UPI ID</label>
      <input
        type="text"
        name="upiId"
        value={upiId}
        onChange={e => setUpiId(e.target.value)}
        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
        placeholder="example@upi"
        required
      />
      <button
        type="submit"
        disabled={loading || disabled}
        className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold text-lg shadow hover:from-pink-600 hover:to-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? '' : 'Pay with UPI'}
      </button>
      {error && <div className="text-red-600 mt-2">{error}</div>}
      {success && <div className="text-green-600 mt-2">{success}</div>}
      {upiStatus && <div className="text-blue-600 mt-2">{upiStatus}</div>}
      {upiRedirectUrl && (
        <div className="mt-2">
          <a href={upiRedirectUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">Open UPI App</a>
        </div>
      )}
      {upiQrCode && (
        <div className="mt-2 flex flex-col items-center">
          <img src={upiQrCode} alt="UPI QR Code" className="w-48 h-48" />
          <div className="text-gray-700 mt-2">Scan this QR code in your UPI app</div>
        </div>
      )}
    </form>
  );
}

const Checkout = () => {
  const { cartItems, clearCart } = useCart();
  
  // Debug cart items on component load
  console.log('Checkout component - cartItems from context:', cartItems);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [error, setError] = useState('');
  const [shipping, setShipping] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [showOrderPopup, setShowOrderPopup] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [user, setUser] = useState(() => {
    try {
      const userFromStorage = JSON.parse(localStorage.getItem('user'));
      const tokenFromStorage = localStorage.getItem('token');
      console.log('Checkout component - user from localStorage:', userFromStorage);
      console.log('Checkout component - token from localStorage:', tokenFromStorage ? 'exists' : 'not found');
      return userFromStorage;
    } catch {
      console.log('Checkout component - error parsing user from localStorage');
      return null;
    }
  });
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [upiId, setUpiId] = useState('');
  const navigate = useNavigate();
  const [clientSecret, setClientSecret] = useState('');

  // Helper to get the correct price for each item
  const getItemPrice = (item) =>
    item.product?.isRental ? (item.product?.securityDeposit ?? 0) : (item.product?.price ?? 0);

  // Calculate total amount in rupees
  const totalAmount = cartItems.reduce((sum, item) => {
    const itemPrice = getItemPrice(item);
    const itemTotal = itemPrice * item.quantity;
    console.log(`Item: ${item.product?.name}, Price: ${itemPrice}, Quantity: ${item.quantity}, Total: ${itemTotal}`);
    return sum + itemTotal;
  }, 0);
  
  console.log('Final totalAmount:', totalAmount);
  console.log('Cart items count:', cartItems.length);
  console.log('Cart items details:', cartItems.map(item => ({
    name: item.product?.name,
    price: item.product?.price,
    isRental: item.product?.isRental,
    securityDeposit: item.product?.securityDeposit,
    quantity: item.quantity,
    calculatedPrice: getItemPrice(item)
  })));

  // Check if cart contains any rental items
  const hasRentalItem = cartItems.some(item => item.product?.isRental);
  const [rentalDisclaimerChecked, setRentalDisclaimerChecked] = useState(false);

  // Add a helper to determine if payment forms should be disabled
  const isPaymentDisabled = hasRentalItem && !rentalDisclaimerChecked;

  // Add a helper to determine if the order button should be disabled
  const isOrderDisabled = loading || (hasRentalItem && !rentalDisclaimerChecked);

  // Compute shipping validity without setting state
  const isShippingValid = React.useMemo(() => {
    return (
      shipping.firstName.trim() &&
      shipping.lastName.trim() &&
      shipping.address.trim() &&
      shipping.city.trim() &&
      shipping.postalCode.trim() &&
      shipping.country.trim()
    );
  }, [shipping]);

  const handleLoginSuccess = (token, userObj) => {
    console.log('Login successful - updating user and closing modal');
    console.log('Saving token to localStorage:', token ? 'token exists' : 'no token');
    console.log('Saving user to localStorage:', userObj);
    
    // Save token and user to localStorage
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userObj));
    
    setUser(userObj);
    setShowLoginModal(false);
    setError('');
  };

  const handleChange = (e) => {
    setShipping({ ...shipping, [e.target.name]: e.target.value });
  };

  const handleUpiChange = (e) => {
    setUpiId(e.target.value);
  };

  const validateShipping = () => {
    if (
      !shipping.firstName.trim() ||
      !shipping.lastName.trim() ||
      !shipping.address.trim() ||
      !shipping.city.trim() ||
      !shipping.postalCode.trim() ||
      !shipping.country.trim()
    ) {
      setError('Please fill in all shipping information fields.');
      return false;
    }
    setError('');
    return true;
  };

  const handleConfirmOrder = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (hasRentalItem && !rentalDisclaimerChecked) {
      setError('You must agree to the rental disclaimer before proceeding.');
      return;
    }
    if (!validateShipping()) return;
    if (paymentMethod === 'card') {
      setError('');
      setLoading(true);
      // 1. Create PaymentMethod
      const [exp_month, exp_year] = expiry.split('/').map(s => s.trim());
      const amount = cartItems.reduce((sum, item) => sum + getItemPrice(item) * item.quantity, 0) * 100;
      let clientSecret = '';
      try {
        const res = await fetch(getApiUrl('/api/payment/create-payment-intent'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount, currency: 'INR', payment_method_types: ['card'] }),
        });
        const data = await res.json();
        clientSecret = data.clientSecret;
        if (!clientSecret) {
          setError('Payment initialization failed. Please try again.');
          setLoading(false);
          return;
        }
      } catch (err) {
        setError('Payment initialization failed. Please try again.');
        setLoading(false);
        return;
      }
      // 3. Confirm payment
      setLoading(false);
      return;
    } else if (paymentMethod === 'cod') {
      setError('');
      setLoading(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setShowLoginModal(true);
        setLoading(false);
        return;
      }

      const currentUser = user;
      const userId = currentUser?._id;
      const userEmail = currentUser?.email;
      const userPhone = currentUser?.mobileNumber;

      if (!userId || !userEmail || !userPhone) {
        setShowLoginModal(true);
        setLoading(false);
        return;
      }

      console.log('Cart items before sending order:', cartItems);

      const cartTotal = cartItems.reduce((sum, item) => sum + getItemPrice(item) * item.quantity, 0);

      try {
        const response = await fetch(`${API_BASE_URL}/api/cart/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            shippingInfo: shipping,
            paymentMethod,
            items: cartItems.map(item => ({
              product: item.product?._id,
              name: item.product?.name,
              quantity: item.quantity,
              price: getItemPrice(item),
              size: item.size,
              color: item.color
            })),
            total: cartTotal,
            user: userId,
            phone: userPhone,
            email: userEmail
          })
        });

        console.log('API Response received. response.ok:', response.ok, 'status:', response.status);
        const data = await response.json();
        console.log('API Response data:', data);

        if (response.ok && data.success) {
          console.log('Success condition met. Calling clearCart()...');
          clearCart();
          console.log('clearCart() completed. Calling setShowOrderPopup(true)...');
          setShowOrderPopup(true);
          console.log('setShowOrderPopup(true) called.');
        } else {
          setError('Order failed: ' + (data.message || response.statusText));
          console.error('Order failed due to API response:', data);
        }
      } catch (err) {
        setError('Order could not be placed. Please check your internet connection.',err);
        console.error('Error placing order (caught in try-catch):', err); // Log full error for debugging
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (paymentMethod === 'card' && cartItems.length > 0) {
      const amount = cartItems.reduce((sum, item) => sum + getItemPrice(item) * item.quantity, 0) * 100;
      fetch(getApiUrl('/api/payment/create-payment-intent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, currency: 'INR', payment_method_types: ['card'] }),
      })
        .then(res => res.json())
        .then(data => {
          setClientSecret(data.clientSecret);
        });
    } else {
      setClientSecret('');
    }
  }, [paymentMethod, cartItems]);

  // Razorpay integration
  const handleRazorpayPayment = async (method = 'card') => {
    console.log('handleRazorpayPayment called - checking authentication...');
    console.log('Current user state:', user);
    console.log('Current showLoginModal state:', showLoginModal);
    
    // Check authentication before proceeding with payment
    const token = localStorage.getItem('token');
    console.log('Token from localStorage:', token ? 'exists' : 'not found');
    
    if (!token) {
      console.log('No token found - showing login modal');
      setShowLoginModal(true);
      console.log('showLoginModal set to true');
      return;
    }

    const currentUser = user;
    const userId = currentUser?._id;
    const userEmail = currentUser?.email;
    const userPhone = currentUser?.mobileNumber;

    console.log('User data check:', { userId, userEmail, userPhone });

    if (!userId || !userEmail || !userPhone) {
      console.log('User data incomplete - showing login modal');
      setShowLoginModal(true);
      console.log('showLoginModal set to true');
      return;
    }

    console.log('Authentication check passed - proceeding with payment');
    console.log('Total amount being sent to Razorpay:', totalAmount);
    console.log('Cart items for debugging:', cartItems);
    console.log('Request body being sent:', { amount: totalAmount });

    // Call backend to create order
    const res = await fetch(getApiUrl('/api/payment/create-order'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: totalAmount }), // totalAmount in rupees
    });
    const order = await res.json();
    console.log('Razorpay order response:', order);

    console.log('Razorpay frontend key:', process.env.REACT_APP_RAZORPAY_KEY_ID);
    console.log('All environment variables:', process.env);
    console.log('Razorpay order details:', order);
    console.log('Is frontend using live mode?', process.env.REACT_APP_RAZORPAY_KEY_ID?.startsWith('rzp_live_'));
    
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_default', // Add this to your frontend .env
      amount: order.amount,
      currency: order.currency,
      order_id: order.id,
      name: "theVastra.store",
      description: "Order Payment",
      method, // Pre-select payment method (card or upi)
      handler: async function (response) {
        // After successful payment, place the order in backend
        setLoading(true);
        const cartTotal = cartItems.reduce((sum, item) => sum + getItemPrice(item) * item.quantity, 0);
        try {
          const responseOrder = await fetch(`${API_BASE_URL}/api/cart/orders`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              shippingInfo: shipping,
              paymentMethod: 'razorpay',
              paymentStatus: 'Paid',
              paymentAmount: cartTotal,
              paymentId: response.razorpay_payment_id,
              items: cartItems.map(item => ({
                product: item.product?._id,
                name: item.product?.name,
                quantity: item.quantity,
                price: getItemPrice(item),
                size: item.size,
                color: item.color
              })),
              total: cartTotal,
              user: currentUser._id,
              phone: currentUser.mobileNumber,
              email: currentUser.email
            })
          });
          const data = await responseOrder.json();
          if (responseOrder.ok && data.success) {
            clearCart();
            setShowOrderPopup(true);
            setTimeout(() => {
              setShowOrderPopup(false);
              navigate('/profile?tab=orders');
            }, 2000);
          } else {
            setError('Order failed: ' + (data.message || responseOrder.statusText));
          }
        } catch (err) {
          setError('Order could not be placed. Please check your internet connection.');
        } finally {
          setLoading(false);
        }
      },
      prefill: {
        name: user?.name,
        email: user?.email,
        contact: user?.phone,
      },
      theme: { color: "#F37254" },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // Debug logging for authentication flow
  if (showLoginModal) {
    console.log('Checkout render - login modal is visible');
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => {
          console.log('Checkout - closing login modal');
          setShowLoginModal(false);
        }}
        onLoginSuccess={handleLoginSuccess}
      />
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

      <div className="max-w-2xl mx-auto">
        {/* Unified Shipping + Payment Card */}
        <div className="bg-white shadow-md rounded-lg p-6 w-full">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Shipping Information</h2>
          {/* Alert for missing details */}
          {error && (
            <div className="mb-4 p-3 bg-pink-100 border border-pink-300 text-pink-700 rounded-lg font-semibold text-center">
              {error}
            </div>
          )}
          {/* Rental Disclaimer Checkbox */}
          {hasRentalItem && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rentalDisclaimerChecked}
                  onChange={e => setRentalDisclaimerChecked(e.target.checked)}
                  className="mt-1 accent-pink-500"
                />
                <span className="text-sm text-gray-700">
                  <strong>Please Note:</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-1 text-xs text-gray-700">
                    <li>This is a rental item, not for purchase.</li>
                    <li>A security deposit may be required, which is refundable upon return inspection.</li>
                    <li>The item must be returned in the same condition as delivered. Any damage, stains, or loss may result in a deduction from the security deposit or additional charges.</li>
                    <li>Late returns may incur a daily penalty as per our rental policy.</li>
                    <li>All items are dry-cleaned and sanitized before dispatch.</li>
                    <li>Rental bookings are non-refundable once confirmed. Rescheduling is allowed based on availability.</li>
                    <li>By placing a rental order, you agree to our <a href="/rental-terms" className="underline text-pink-600" target="_blank" rel="noopener noreferrer">Rental Terms & Conditions</a>.</li>
                  </ul>
                </span>
              </label>
            </div>
          )}
          <form className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={shipping.firstName}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={shipping.lastName}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <input
                type="text"
                name="address"
                value={shipping.address}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">City</label>
                <input
                  type="text"
                  name="city"
                  value={shipping.city}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Postal Code</label>
                <input
                  type="text"
                  name="postalCode"
                  value={shipping.postalCode}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <input
                type="text"
                name="country"
                value={shipping.country}
                onChange={handleChange}
                required
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
              />
            </div>
          </form>

          {/* Payment Method Selection (now inside the same card) */}
          <div className="mt-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Method</h2>
            <div className="flex flex-row gap-4 items-center justify-start flex-wrap">
              <div className="flex gap-4">
                <button
                  type="button"
                  className={`flex flex-col items-center px-6 py-4 rounded-xl border-2 transition-all duration-200 shadow-sm cursor-pointer focus:outline-none ${paymentMethod === 'card' ? 'border-pink-500 bg-pink-50 ring-2 ring-pink-200' : 'border-gray-200 bg-white hover:border-pink-300'}`}
                  onClick={() => setPaymentMethod('card')}
                >
                  <svg className="w-8 h-8 mb-2 text-pink-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/><path d="M2 11h20" stroke="currentColor" strokeWidth="2"/></svg>
                  <span className="font-semibold text-gray-900">Card Payment</span>
                </button>
                <button
                  type="button"
                  className={`flex flex-col items-center px-6 py-4 rounded-xl border-2 transition-all duration-200 shadow-sm cursor-pointer focus:outline-none ${paymentMethod === 'cod' ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-gray-200 bg-white hover:border-indigo-300'}`}
                  onClick={() => setPaymentMethod('cod')}
                >
                  <svg className="w-8 h-8 mb-2 text-indigo-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 9V7a5 5 0 00-10 0v2" stroke="currentColor" strokeWidth="2"/><rect x="5" y="9" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/></svg>
                  <span className="font-semibold text-gray-900">Cash on Delivery</span>
                </button>
              </div>
            </div>
          </div>

          {/* Payment action buttons (now inside the same card) */}
          <div className="mt-8">
            {paymentMethod === 'card' && (
              <button
                onClick={() => {
                  console.log('Pay with Card button clicked');
                  handleRazorpayPayment('card');
                }}
                disabled={isPaymentDisabled || loading || !isShippingValid}
                className="w-full mt-2 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold text-lg shadow hover:from-pink-600 hover:to-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '' : 'Pay with Card'}
              </button>
            )}
            {paymentMethod === 'cod' && (
              <div className="space-y-4">
                <p className="text-green-700 font-semibold">You have selected Cash on Delivery. Please confirm your order.</p>
                {error && <div className="text-red-600 font-semibold mb-4">{error}</div>}
                <button
                  type="submit"
                  disabled={isOrderDisabled}
                  className={`w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold text-lg shadow hover:from-pink-600 hover:to-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed`}
                  onClick={handleConfirmOrder}
                >
                  {loading ? '' : 'Confirm Order'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {showOrderPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center animate-bounce-in">
            <svg className="w-16 h-16 text-green-500 mb-4 animate-ping-once" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2l4-4" />
            </svg>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Order Confirmed!</h2>
            <p className="text-gray-700 mb-4 text-center">Thank you for your purchase.<br />A confirmation email and SMS have been sent.</p>
            <button
              className="mt-2 px-6 py-2 bg-gradient-to-r from-pink-500 to-indigo-500 text-white rounded-lg font-semibold shadow hover:from-pink-600 hover:to-indigo-600 transition"
              onClick={() => {
                setShowOrderPopup(false);
                navigate('/profile');
              }}
            >
              View My Orders
            </button>
          </div>
          <style>
            {`
              @keyframes bounce-in {
                0% { transform: scale(0.7); opacity: 0; }
                60% { transform: scale(1.05); opacity: 1; }
                100% { transform: scale(1); }
              }
              .animate-bounce-in { animation: bounce-in 0.5s cubic-bezier(.68,-0.55,.27,1.55) both; }
              @keyframes ping-once {
                0% { transform: scale(1); opacity: 1; }
                50% { transform: scale(1.2); opacity: 0.7; }
                100% { transform: scale(1); opacity: 1; }
              }
              .animate-ping-once { animation: ping-once 0.8s linear; }
            `}
          </style>
        </div>
      )}
    </div>
  );
};

export default Checkout; 